import { IProjectApplicantRequest } from '../interfaces/projectApplicantRequest';
import { IProjectApplicant } from '../interfaces/projectApplicant';
import { axiosAdmin } from './axios';
import { IProjectOpportunity } from '../interfaces/projectOpportunity';
import { ICreateProjectRequest } from '../interfaces/createProjectRequest';
import { IBaseProjectPosition, ICreateProjectPosition, IProjectPosition } from '../interfaces/projectPosition';
import { IProjectPositionSearchRequest, IProjectPositionSearchResponse } from '../interfaces/projectPostitionSearch';
import { IListResponse } from '../interfaces/listResponse';

export namespace ProjectOpportunityService {
  const baseClientUrl = (clientId: number) => `/clients/${clientId}/opportunities/projects`;

  interface IBaseProps {
    clientId: number;
    projectOpportunityId: string;
    positionId: string;
  }

  export function getClientProjects(clientId: number): Promise<IProjectOpportunity[]> {
    return axiosAdmin.get<IProjectOpportunity[]>(baseClientUrl(clientId))
      .then(({ data }) => data);
  }

  export function createNewProject(clientId: number, project: ICreateProjectRequest): Promise<IProjectOpportunity> {
    return axiosAdmin.post<IProjectOpportunity>(baseClientUrl(clientId), project)
      .then(({ data }) => data);
  }

  export function getProjectPositions({
                                        clientId,
                                        projectOpportunityId
                                      }: Omit<IBaseProps, 'positionId'>): Promise<IProjectPosition[]> {
    return axiosAdmin.get<IProjectPosition[]>(baseClientUrl(clientId) + `/${projectOpportunityId}/positions`)
      .then(({ data }) => data);
  }

  export function addPositionToProject({
                                         clientId,
                                         projectOpportunityId,
                                         position
                                       }: Omit<IBaseProps, 'positionId'> & { position: ICreateProjectPosition }): Promise<IProjectPosition> {
    return axiosAdmin.post<IProjectPosition>(baseClientUrl(clientId) + `/${projectOpportunityId}/positions`, position)
      .then(({ data }) => data);
  }

  export function addSkillsToPosition({
                                        clientId,
                                        positionId,
                                        projectOpportunityId,
                                        skills
                                      }: IBaseProps & { skills: string[] }): Promise<IProjectPosition> {
    return axiosAdmin.post<IProjectPosition>(baseClientUrl(clientId) + `/${projectOpportunityId}/positions/${positionId}/skills`, skills)
      .then(({ data }) => data);
  }

  export function deleteSkillsFromPosition({
                                             clientId,
                                             positionId,
                                             projectOpportunityId,
                                             skills
                                           }: IBaseProps & { skills: string[] }): Promise<IProjectPosition> {
    return axiosAdmin.delete<IProjectPosition>(baseClientUrl(clientId) + `/${projectOpportunityId}/positions/${positionId}/skills`, { data: skills })
      .then(({ data }) => data);
  }

  export function getAllPositions(params: IProjectPositionSearchRequest): Promise<IListResponse<IProjectPositionSearchResponse>> {
    return axiosAdmin.post<IListResponse<IProjectPositionSearchResponse>>('/clients/opportunities/projects/positions/search', params)
      .then(({ data }) => data);
  }

  export function getProjectDetails({ clientId, projectOpportunityId }: Omit<IBaseProps, 'positionId'>): Promise<IProjectOpportunity> {
    return axiosAdmin.get<IProjectOpportunity>(baseClientUrl(clientId) + `/${projectOpportunityId}`)
      .then(({ data }) => data);
  }

  interface IUpdateProjectProps<T = Partial<Omit<IProjectOpportunity, 'id' | 'customerId' | 'status'>>> extends Omit<IBaseProps, 'positionId'> {
    data: T;
  }

  export function updateProjectDetails({ clientId, projectOpportunityId, data }: IUpdateProjectProps): Promise<IProjectOpportunity> {
    return axiosAdmin.patch<IProjectOpportunity>(baseClientUrl(clientId) + `/${projectOpportunityId}`, data)
      .then(({ data }) => data);
  }

  export function deletePosition({ clientId, positionId, projectOpportunityId }: IBaseProps): Promise<IProjectPosition> {
    return axiosAdmin.delete<IProjectPosition>(baseClientUrl(clientId) + `/${projectOpportunityId}/positions/${positionId}`)
      .then(({ data }) => data);
  }

  export function editPosition({
                                 clientId,
                                 projectOpportunityId,
                                 positionId,
                                 data
                               }: IBaseProps & { data: IBaseProjectPosition }): Promise<IProjectPosition> {
    return axiosAdmin.patch<IProjectPosition>(baseClientUrl(clientId) + `/${projectOpportunityId}/positions/${positionId}`, data)
      .then(({ data }) => data);
  }

  export function getPositionApplicants({
                                          clientId,
                                          projectOpportunityId,
                                          positionId
                                        }: IBaseProps): Promise<IProjectApplicant[]> {
    return axiosAdmin.get<IProjectApplicant[]>(baseClientUrl(clientId) + `/${projectOpportunityId}/positions/applicants`, { params: { positionId } })
      .then(({ data }) => data);
  }

  export function addApplicantFreelancer(data: IProjectApplicantRequest): Promise<IProjectApplicant> {
    return axiosAdmin.post<IProjectApplicant>('/opportunities/projects/applicants', { ...data, fromAdmin: true })
      .then(({ data }) => data);
  }

}